import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch,
  FiUser,
  FiMail,
  FiPlay,
  FiPause,
  FiVolume2,
  FiVolumeX,
  FiFileText,
  FiX,
  FiRefreshCw,
  FiMusic,
  FiChevronDown,
  FiChevronUp,
  FiArrowLeft,
  FiUsers,
  FiShield,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import MaterialPagination from './MaterialPagination';
import { useSidebar } from '../contexts/SidebarContext';
import {
  authAPI,
  UserProfile,
  adminUserManagementAPI,
  UserDetails,
  AudioFile,
  AdminUserListItem,
  AdminUsersStats,
} from '../services/api';
import './UserManagement.css';
import './Dashboard.css';

interface UserManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

type StatusFilter = 'all' | 'active' | 'inactive';
type RoleFilter = 'all' | 'staff' | 'users';
type SubFilter = 'all' | 'subscribed' | 'none';

const PAGE_SIZES = [25, 50, 100];

const UserManagement: React.FC<UserManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    id: 0,
    email: userEmail || '',
    organization: 'NAATI',
    first_name: '',
    is_superuser: false,
  });

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [subFilter, setSubFilter] = useState<SubFilter>('all');
  const [ordering, setOrdering] = useState('-created_at');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminUsersStats | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [activeTab, setActiveTab] = useState<'overview' | 'recordings' | 'segments'>('overview');
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(new Set());
  const [segmentSearchQuery, setSegmentSearchQuery] = useState('');
  const segmentRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    fetchUserProfile();
    fetchStats();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedUser) return;
    fetchUsers();
  }, [selectedUser, page, pageSize, searchTerm, statusFilter, roleFilter, subFilter, ordering]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setPlayingRecordingId(null);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [playingRecordingId]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        id: profile.id,
        email: profile.email || userEmail || '',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_superuser: profile.is_superuser || false,
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchStats = async () => {
    try {
      setStats(await adminUserManagementAPI.getStats());
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setListLoading(true);
      setListError(null);
      const res = await adminUserManagementAPI.listUsers({
        page,
        page_size: pageSize,
        q: searchTerm || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        is_staff: roleFilter === 'all' ? undefined : roleFilter === 'staff',
        has_subscription: subFilter === 'all' ? undefined : subFilter === 'subscribed',
        ordering,
      });
      setUsers(res.results);
      setTotalPages(res.total_pages);
      setTotalCount(res.count);
    } catch (error: any) {
      setListError(error.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleUserSelect = async (user: UserProfile) => {
    setSelectedUser(user);
    setLoadingDetails(true);
    setDetailsError(null);

    try {
      const details = await adminUserManagementAPI.getUserDetails(user.id);
      setUserDetails(details);
      setActiveTab('overview');
    } catch (error: any) {
      setDetailsError(error.message || 'Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setSelectedUser(null);
    setUserDetails(null);
    setPlayingRecordingId(null);
    handleStopRecording();
    fetchUsers();
    fetchStats();
  };

  const handleToggleActive = async (user: AdminUserListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    if (user.id === userInfo.id) {
      window.alert('You cannot deactivate your own account.');
      return;
    }
    const next = !user.is_active;
    if (!next && !window.confirm(`Deactivate ${user.email || user.username || `user #${user.id}`}? They will not be able to sign in.`)) {
      return;
    }
    try {
      setUpdatingId(user.id);
      const updated = await adminUserManagementAPI.updateUser(user.id, { is_active: next });
      setUsers((prev) => prev.map((row) => (row.id === user.id ? { ...row, ...updated } : row)));
      fetchStats();
    } catch (error: any) {
      window.alert(error.message || 'Failed to update user');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleStaff = async (user: AdminUserListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!userInfo.is_superuser) return;
    if (user.id === userInfo.id) {
      window.alert('You cannot remove your own staff access.');
      return;
    }
    try {
      setUpdatingId(user.id);
      const updated = await adminUserManagementAPI.updateUser(user.id, { is_staff: !user.is_staff });
      setUsers((prev) => prev.map((row) => (row.id === user.id ? { ...row, ...updated } : row)));
      fetchStats();
    } catch (error: any) {
      window.alert(error.message || 'Failed to update staff status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePlayRecording = (recording: AudioFile) => {
    if (playingRecordingId === recording.id) {
      const audio = audioRef.current;
      if (audio) {
        if (isPlaying) {
          audio.pause();
          setIsPlaying(false);
        } else {
          audio.play();
          setIsPlaying(true);
        }
      }
    } else {
      setPlayingRecordingId(recording.id);
      setIsPlaying(true);
      setTimeout(() => {
        const audio = audioRef.current;
        if (audio && recording.stream_url) {
          audio.src = recording.stream_url;
          audio.volume = isMuted ? 0 : volume;
          audio.play();
        }
      }, 100);
    }
  };

  const handleStopRecording = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setPlayingRecordingId(null);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume;
    }
  };

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newMuted ? 0 : volume;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const displayName = (user: UserProfile) => {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.username || user.email || `User #${user.id}`;
  };

  const initials = (user: UserProfile) => {
    const name = displayName(user);
    return name.slice(0, 2).toUpperCase();
  };

  const toggleOrdering = (field: string) => {
    setPage(1);
    setOrdering((prev) => (prev === field ? `-${field}` : prev === `-${field}` ? field : `-${field}`));
  };

  const sortMark = (field: string) => {
    if (ordering === field) return ' ↑';
    if (ordering === `-${field}`) return ' ↓';
    return '';
  };

  const rowIndex = (index: number) => (page - 1) * pageSize + index + 1;

  const filtersActive = Boolean(searchTerm || statusFilter !== 'all' || roleFilter !== 'all' || subFilter !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('all');
    setRoleFilter('all');
    setSubFilter('all');
    setOrdering('-created_at');
    setPage(1);
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />

      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />

        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <div className="user-management-container">
              {!selectedUser ? (
                <>
                  <div className="page-header um-header">
                    <div>
                      <p className="um-kicker">Staff desk</p>
                      <h1 className="page-title">User Management</h1>
                      <p className="um-lede">Search, filter, and inspect every account without loading the full roster at once.</p>
                    </div>
                    <button type="button" className="um-refresh-btn" onClick={() => { fetchUsers(); fetchStats(); }} disabled={listLoading}>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
                        size: 16,
                        className: listLoading ? 'spinner' : undefined,
                      })}
                      Refresh
                    </button>
                  </div>

                  <div className="um-stats" aria-label="User counts">
                    <button type="button" className={`um-stat ${statusFilter === 'all' && roleFilter === 'all' && subFilter === 'all' ? 'is-active' : ''}`} onClick={clearFilters}>
                      <span className="um-stat-value">{stats?.total ?? '—'}</span>
                      <span className="um-stat-label">Total</span>
                    </button>
                    <button type="button" className={`um-stat ${statusFilter === 'active' ? 'is-active' : ''}`} onClick={() => { setPage(1); setStatusFilter(statusFilter === 'active' ? 'all' : 'active'); }}>
                      <span className="um-stat-value">{stats?.active ?? '—'}</span>
                      <span className="um-stat-label">Active</span>
                    </button>
                    <button type="button" className={`um-stat ${statusFilter === 'inactive' ? 'is-active' : ''}`} onClick={() => { setPage(1); setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive'); }}>
                      <span className="um-stat-value">{stats?.inactive ?? '—'}</span>
                      <span className="um-stat-label">Inactive</span>
                    </button>
                    <button type="button" className={`um-stat ${roleFilter === 'staff' ? 'is-active' : ''}`} onClick={() => { setPage(1); setRoleFilter(roleFilter === 'staff' ? 'all' : 'staff'); }}>
                      <span className="um-stat-value">{stats?.staff ?? '—'}</span>
                      <span className="um-stat-label">Staff</span>
                    </button>
                    <button type="button" className={`um-stat ${subFilter === 'subscribed' ? 'is-active' : ''}`} onClick={() => { setPage(1); setSubFilter(subFilter === 'subscribed' ? 'all' : 'subscribed'); }}>
                      <span className="um-stat-value">{stats?.subscribed ?? '—'}</span>
                      <span className="um-stat-label">Subscribed</span>
                    </button>
                  </div>

                  <div className="um-toolbar">
                    <div className="um-search">
                      {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <input
                        type="search"
                        placeholder="Search email, username, name, or organization"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        aria-label="Search users"
                      />
                      {searchInput && (
                        <button type="button" className="um-search-clear" onClick={() => setSearchInput('')} aria-label="Clear search">
                          {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 16 })}
                        </button>
                      )}
                    </div>

                    <div className="um-filters">
                      <label>
                        Status
                        <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value as StatusFilter); }}>
                          <option value="all">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </label>
                      <label>
                        Role
                        <select value={roleFilter} onChange={(e) => { setPage(1); setRoleFilter(e.target.value as RoleFilter); }}>
                          <option value="all">All</option>
                          <option value="staff">Staff</option>
                          <option value="users">Members</option>
                        </select>
                      </label>
                      <label>
                        Access
                        <select value={subFilter} onChange={(e) => { setPage(1); setSubFilter(e.target.value as SubFilter); }}>
                          <option value="all">All</option>
                          <option value="subscribed">Subscribed</option>
                          <option value="none">No subscription</option>
                        </select>
                      </label>
                      <label>
                        Rows
                        <select value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                          {PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </label>
                      {filtersActive && (
                        <button type="button" className="um-clear-filters" onClick={clearFilters}>
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="um-status-line">
                    {listLoading ? 'Loading roster…' : `${totalCount} account${totalCount === 1 ? '' : 's'} · page ${page} of ${Math.max(totalPages, 1)}`}
                  </p>

                  {listError && (
                    <div className="error-container um-list-error">
                      <p>{listError}</p>
                      <button type="button" className="um-refresh-btn" onClick={fetchUsers}>Retry</button>
                    </div>
                  )}

                  <div className="um-table-wrap">
                    <table className="um-table">
                      <thead>
                        <tr>
                          <th className="um-col-index">#</th>
                          <th>
                            <button type="button" className="um-sort" onClick={() => toggleOrdering('first_name')}>
                              User{sortMark('first_name')}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="um-sort" onClick={() => toggleOrdering('email')}>
                              Email{sortMark('email')}
                            </button>
                          </th>
                          <th>Organization</th>
                          <th>Flags</th>
                          <th>
                            <button type="button" className="um-sort" onClick={() => toggleOrdering('created_at')}>
                              Joined{sortMark('created_at')}
                            </button>
                          </th>
                          <th>
                            <button type="button" className="um-sort" onClick={() => toggleOrdering('last_login')}>
                              Last login{sortMark('last_login')}
                            </button>
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listLoading ? (
                          Array.from({ length: 8 }).map((_, i) => (
                            <tr key={`sk-${i}`} className="um-skeleton-row">
                              <td colSpan={8}><div className="um-skeleton" /></td>
                            </tr>
                          ))
                        ) : users.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="um-empty">
                              {React.createElement(FiUsers as React.ComponentType<{ size?: number }>, { size: 28 })}
                              <span>{filtersActive ? 'No users match these filters.' : 'No users yet.'}</span>
                            </td>
                          </tr>
                        ) : (
                          users.map((user, index) => (
                            <tr key={user.id} onClick={() => handleUserSelect(user)}>
                              <td className="um-col-index">{rowIndex(index)}</td>
                              <td>
                                <div className="um-user-cell">
                                  <span className="um-avatar">{initials(user)}</span>
                                  <span>
                                    <span className="um-user-name">{displayName(user)}</span>
                                    {user.username && user.email && (
                                      <span className="um-user-sub">@{user.username}</span>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="um-mono">{user.email || '—'}</td>
                              <td>{user.organization || '—'}</td>
                              <td>
                                <div className="um-chips">
                                  <span className={`um-chip ${user.is_active ? 'is-ok' : 'is-off'}`}>
                                    {user.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  {user.is_staff && <span className="um-chip is-staff">Staff</span>}
                                  <span className={`um-chip ${user.has_active_subscription ? 'is-sub' : 'is-muted'}`}>
                                    {user.has_active_subscription ? 'Subscribed' : 'Free'}
                                  </span>
                                </div>
                              </td>
                              <td className="um-mono">{formatShortDate(user.created_at)}</td>
                              <td className="um-mono">{formatShortDate(user.last_login)}</td>
                              <td>
                                <div className="um-row-actions">
                                  <button type="button" className="um-row-btn" onClick={(e) => { e.stopPropagation(); handleUserSelect(user); }}>
                                    Open
                                  </button>
                                  <button
                                    type="button"
                                    className="um-row-btn"
                                    disabled={updatingId === user.id || user.id === userInfo.id}
                                    onClick={(e) => handleToggleActive(user, e)}
                                  >
                                    {user.is_active ? 'Deactivate' : 'Activate'}
                                  </button>
                                  {userInfo.is_superuser && (
                                    <button
                                      type="button"
                                      className="um-row-btn"
                                      disabled={updatingId === user.id || user.id === userInfo.id}
                                      onClick={(e) => handleToggleStaff(user, e)}
                                    >
                                      {React.createElement(FiShield as React.ComponentType<{ size?: number }>, { size: 14 })}
                                      {user.is_staff ? 'Unstaff' : 'Make staff'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <MaterialPagination
                    page={page}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={setPage}
                    disabled={listLoading}
                  />
                </>
              ) : (
                <motion.div
                  className="user-details-section"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <button type="button" className="um-back-btn" onClick={closeDetails}>
                    {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
                    Back to roster
                  </button>

                  {loadingDetails ? (
                    <div className="loading-container">
                      <div className="spinner-large" />
                      <p>Loading user details...</p>
                    </div>
                  ) : detailsError ? (
                    <div className="error-container">
                      <p>{detailsError}</p>
                    </div>
                  ) : userDetails ? (
                    <>
                      <div className="user-info-card">
                        <div className="user-header">
                          <div className="user-avatar">
                            {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 32 })}
                          </div>
                          <div className="user-info">
                            <h2 className="user-name">{displayName(userDetails.user)}</h2>
                            <div className="user-email">
                              {React.createElement(FiMail as React.ComponentType<{ size?: number }>, { size: 16 })}
                              <span>{userDetails.user.email || userDetails.user.username || '—'}</span>
                            </div>
                            {userDetails.user.organization && (
                              <div className="user-org">
                                {React.createElement(FiFileText as React.ComponentType<{ size?: number }>, { size: 16 })}
                                <span>{userDetails.user.organization}</span>
                              </div>
                            )}
                            <div className="user-stats">
                              <div className="stat-item">
                                <span className="stat-label">Total Recordings:</span>
                                <span className="stat-value">{userDetails.total_recordings}</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-label">Segments Worked On:</span>
                                <span className="stat-value">{userDetails.total_segments}</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-label">Member Since:</span>
                                <span className="stat-value">{formatDate(userDetails.user.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <button className="close-user-btn" onClick={closeDetails} aria-label="Close user details">
                            {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 20 })}
                          </button>
                        </div>
                      </div>

                      <div className="user-tabs">
                        <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                          Overview
                        </button>
                        <button className={`tab-btn ${activeTab === 'recordings' ? 'active' : ''}`} onClick={() => setActiveTab('recordings')}>
                          All Recordings ({userDetails.total_recordings})
                        </button>
                        <button className={`tab-btn ${activeTab === 'segments' ? 'active' : ''}`} onClick={() => setActiveTab('segments')}>
                          Segments ({userDetails.total_segments})
                        </button>
                      </div>

                      <div className="tab-content">
                        {activeTab === 'overview' && (
                          <div className="overview-tab">
                            <div className="overview-stats">
                              <div className="stat-card">
                                <div className="stat-icon">
                                  {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 24 })}
                                </div>
                                <div className="stat-info">
                                  <div className="stat-value">{userDetails.total_recordings}</div>
                                  <div className="stat-label">Total Recordings</div>
                                </div>
                              </div>
                              <div className="stat-card">
                                <div className="stat-icon">
                                  {React.createElement(FiFileText as React.ComponentType<{ size?: number }>, { size: 24 })}
                                </div>
                                <div className="stat-info">
                                  <div className="stat-value">{userDetails.total_segments}</div>
                                  <div className="stat-label">Segments</div>
                                </div>
                              </div>
                            </div>

                            {userDetails.segments.length > 0 && (
                              <div className="recent-segments">
                                <h3>Recent Segments</h3>
                                <div className="segments-list">
                                  {userDetails.segments.slice(0, 5).map((segmentData) => (
                                    <div
                                      key={segmentData.segment.id}
                                      className="segment-card clickable"
                                      onClick={() => {
                                        setActiveTab('segments');
                                        setExpandedSegments((prev) => new Set(prev).add(segmentData.segment.id));
                                        setTimeout(() => {
                                          segmentRefs.current.get(segmentData.segment.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }, 300);
                                      }}
                                    >
                                      <div className="segment-header">
                                        <h4>{segmentData.segment.name}</h4>
                                        <span className="segment-badge">
                                          {segmentData.recordings.length} recording{segmentData.recordings.length !== 1 ? 's' : ''}
                                        </span>
                                      </div>
                                      {segmentData.segment.description && (
                                        <p className="segment-description">{segmentData.segment.description}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {activeTab === 'recordings' && (
                          <div className="recordings-tab">
                            {userDetails.all_recordings.length === 0 ? (
                              <div className="empty-state"><p>No recordings found for this user.</p></div>
                            ) : (
                              <div className="recordings-list">
                                {userDetails.all_recordings.map((recording) => (
                                  <div key={recording.id} className={`recording-item ${playingRecordingId === recording.id ? 'playing' : ''}`}>
                                    <div className="recording-info">
                                      <div className="recording-name">{recording.file_name}</div>
                                      <div className="recording-meta">
                                        <span>{formatDate(recording.created_at)}</span>
                                        <span>•</span>
                                        <span>{recording.file_size_mb} MB</span>
                                        {recording.duration ? (
                                          <>
                                            <span>•</span>
                                            <span>{formatTime(recording.duration)}</span>
                                          </>
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className="recording-actions">
                                      <button className="play-btn" onClick={() => handlePlayRecording(recording)} disabled={!recording.stream_url}>
                                        {playingRecordingId === recording.id && isPlaying
                                          ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 20 })
                                          : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 20 })}
                                      </button>
                                      {playingRecordingId === recording.id && (
                                        <div className="audio-progress">
                                          <span className="time-display">
                                            {formatTime(currentTime)} / {formatTime(duration || recording.duration || 0)}
                                          </span>
                                          <input
                                            type="range"
                                            min="0"
                                            max={duration || recording.duration || 0}
                                            value={currentTime}
                                            onChange={(e) => {
                                              const newTime = parseFloat(e.target.value);
                                              setCurrentTime(newTime);
                                              if (audioRef.current) audioRef.current.currentTime = newTime;
                                            }}
                                            className="progress-slider"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {activeTab === 'segments' && (
                          <div className="segments-tab">
                            {userDetails.segments.length === 0 ? (
                              <div className="empty-state"><p>No segments found for this user.</p></div>
                            ) : (
                              <>
                                <div className="segment-search-section">
                                  <div className="segment-search-input-wrapper">
                                    <span className="search-icon">
                                      {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 20 })}
                                    </span>
                                    <input
                                      type="text"
                                      className="segment-search-input"
                                      placeholder="Search segments by name..."
                                      value={segmentSearchQuery}
                                      onChange={(e) => setSegmentSearchQuery(e.target.value)}
                                    />
                                    {segmentSearchQuery && (
                                      <button className="clear-search-btn" onClick={() => setSegmentSearchQuery('')}>
                                        {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 18 })}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="segments-detailed-list">
                                  {userDetails.segments
                                    .filter((segmentData) => {
                                      if (!segmentSearchQuery.trim()) return true;
                                      const query = segmentSearchQuery.toLowerCase();
                                      return (
                                        segmentData.segment.name.toLowerCase().includes(query) ||
                                        (segmentData.segment.description || '').toLowerCase().includes(query)
                                      );
                                    })
                                    .map((segmentData) => {
                                      const isExpanded = expandedSegments.has(segmentData.segment.id);
                                      return (
                                        <div
                                          key={segmentData.segment.id}
                                          className="segment-detail-card"
                                          ref={(el) => {
                                            if (el) segmentRefs.current.set(segmentData.segment.id, el);
                                            else segmentRefs.current.delete(segmentData.segment.id);
                                          }}
                                        >
                                          <div
                                            className={`segment-detail-header ${isExpanded ? 'expanded' : ''}`}
                                            onClick={() => {
                                              setExpandedSegments((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(segmentData.segment.id)) next.delete(segmentData.segment.id);
                                                else next.add(segmentData.segment.id);
                                                return next;
                                              });
                                            }}
                                          >
                                            <div className="segment-header-left">
                                              <button className="expand-toggle-btn" type="button">
                                                {isExpanded
                                                  ? React.createElement(FiChevronUp as React.ComponentType<{ size?: number }>, { size: 20 })
                                                  : React.createElement(FiChevronDown as React.ComponentType<{ size?: number }>, { size: 20 })}
                                              </button>
                                              <div>
                                                <h3>{segmentData.segment.name}</h3>
                                                {segmentData.segment.description && (
                                                  <p className="segment-description">{segmentData.segment.description}</p>
                                                )}
                                              </div>
                                            </div>
                                            <div className="segment-stats">
                                              <span className="stat-badge">
                                                {segmentData.recordings.length} Recording{segmentData.recordings.length !== 1 ? 's' : ''}
                                              </span>
                                              <span className="stat-badge">
                                                {segmentData.total_attempts} Attempt{segmentData.total_attempts !== 1 ? 's' : ''}
                                              </span>
                                            </div>
                                          </div>
                                          {isExpanded && segmentData.recordings.length > 0 && (
                                            <div className="segment-recordings">
                                              <h4>Recordings for this segment:</h4>
                                              {segmentData.recordings.map((recordingData, idx) => (
                                                <div key={idx} className="segment-recording-item">
                                                  <div className="recording-header">
                                                    <div className="recording-order">
                                                      Audio {recordingData.order !== null ? recordingData.order + 1 : idx + 1}
                                                    </div>
                                                    <div className="recording-meta">
                                                      <span>{recordingData.attempts} attempt{recordingData.attempts !== 1 ? 's' : ''}</span>
                                                      {recordingData.last_attempt_at && (
                                                        <>
                                                          <span>•</span>
                                                          <span>{formatDate(recordingData.last_attempt_at)}</span>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div className="recording-comparison">
                                                    <div className="audio-comparison-item">
                                                      <div className="audio-label">System Audio:</div>
                                                      <div className="audio-name">{recordingData.system_audio.file_name}</div>
                                                      <div className="audio-duration">
                                                        {recordingData.system_audio.duration ? formatTime(recordingData.system_audio.duration) : 'N/A'}
                                                      </div>
                                                      <button className="play-recording-btn" onClick={() => handlePlayRecording(recordingData.system_audio)} disabled={!recordingData.system_audio.stream_url}>
                                                        {playingRecordingId === recordingData.system_audio.id && isPlaying
                                                          ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 18 })
                                                          : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                                                      </button>
                                                    </div>
                                                    <div className="audio-comparison-item">
                                                      <div className="audio-label">User Recording:</div>
                                                      {recordingData.recording ? (
                                                        <>
                                                          <div className="audio-name">{recordingData.recording.file_name}</div>
                                                          <div className="audio-duration">
                                                            {recordingData.recording.duration ? formatTime(recordingData.recording.duration) : 'N/A'}
                                                          </div>
                                                          <button className="play-recording-btn" onClick={() => handlePlayRecording(recordingData.recording!)} disabled={!recordingData.recording.stream_url}>
                                                            {playingRecordingId === recordingData.recording.id && isPlaying
                                                              ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 18 })
                                                              : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                                                          </button>
                                                        </>
                                                      ) : (
                                                        <div className="no-recording"><span>No recording available</span></div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </motion.div>
              )}

              <audio
                ref={audioRef}
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onEnded={() => {
                  setIsPlaying(false);
                  setCurrentTime(0);
                  setPlayingRecordingId(null);
                }}
              />

              {playingRecordingId && (
                <div className="volume-control-panel">
                  <button className="volume-btn" onClick={toggleMute}>
                    {isMuted
                      ? React.createElement(FiVolumeX as React.ComponentType<{ size?: number }>, { size: 20 })
                      : React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 20 })}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                  <span className="volume-value">{Math.round((isMuted ? 0 : volume) * 100)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
